import { useState, useEffect, useCallback, useRef } from "react";
import { X, Save, LogOut, LogIn, FileEdit, ChevronDown, ChevronRight, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import MsgObsAcessoManagerModal from "@/components/MsgObsAcessoManagerModal";

/**
 * Modal "Observações e Saídas" do visitante.
 *
 * Carrega:
 * - Dados visíveis: nome, placa/modelo/cor, data_cadastro, data_hora_entrada
 * - Observação do CPF (cliente_obs_cpf)
 * - Observação do Cadastro (cades_obs_registro)
 *
 * Ações de rodapé:
 * - Cancelar (ESC): fecha sem salvar
 * - Salvar: persiste mudanças nas duas observações
 * - Registrar Saída: persiste observações + chama POST /baixar (data/hora no fuso do cliente)
 */
export default function VisitanteObsSaidaModal({ open, onOpenChange, visitante, onUpdated }) {
  const [obsCpf, setObsCpf] = useState("");
  const [obsCpfOriginal, setObsCpfOriginal] = useState("");
  const [obsCadastro, setObsCadastro] = useState("");
  const [obsCadastroOriginal, setObsCadastroOriginal] = useState("");
  const [historico, setHistorico] = useState([]);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [obsHistorico, setObsHistorico] = useState([]);
  const [obsHistoricoOpen, setObsHistoricoOpen] = useState(false);
  const [obsCpfOpen, setObsCpfOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSaida, setSavingSaida] = useState(false);
  const [savingEntrada, setSavingEntrada] = useState(false);
  const [msgObsManagerOpen, setMsgObsManagerOpen] = useState(false);
  const dirtyRef = useRef({ cpf: false, registro: false });

  // Obs do Cadastro obrigatória quando a Saída acontece FORA da validade E em outro dia
  // (regra de governança: justificar saída tardia que cruzou a meia-noite).
  // Se expirou no mesmo dia, observação permanece OPCIONAL.
  const obsCadastroObrigatoria = (() => {
    if (!visitante?.permanencia_final) return false;
    const pf = new Date(visitante.permanencia_final);
    if (isNaN(pf.getTime())) return false;
    const agora = new Date();
    if (agora <= pf) return false; // ainda dentro do prazo
    const mesmoDia = pf.getFullYear() === agora.getFullYear()
      && pf.getMonth() === agora.getMonth()
      && pf.getDate() === agora.getDate();
    return !mesmoDia;
  })();

  // Portaria atual não aceita Prestadores E o visitante é Prestador (1) ou Entregador (5)?
  // Adicionalmente, respeita a janela horária `bloqueio_prestador_inicio`/`bloqueio_prestador_fim`
  // (HH:MM). Se ambos vazios → bloqueio 24h (comportamento default).
  // Suporta janela que cruza a meia-noite (fim < início → passa por 00:00).
  const obsPrestadorObrigatoria = (() => {
    try {
      const portaria = JSON.parse(sessionStorage.getItem("brave_cliente_portaria") || "null");
      if (!portaria) return false;
      if (portaria.aceita_prestadores !== false) return false;
      const tipo = visitante?.tipo_cadastro;
      if (tipo !== 1 && tipo !== 5) return false;
      // Sem janela definida → bloqueio 24h
      const ini = (portaria.bloqueio_prestador_inicio || "").trim();
      const fim = (portaria.bloqueio_prestador_fim || "").trim();
      if (!ini || !fim) return true;
      // Compara com o horário local (fuso do operador). Formato HH:MM → minutos desde 00:00.
      const toMin = (hhmm) => {
        const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10) || 0);
        return h * 60 + m;
      };
      const agora = new Date();
      const nowMin = agora.getHours() * 60 + agora.getMinutes();
      const iniMin = toMin(ini);
      const fimMin = toMin(fim);
      if (iniMin <= fimMin) {
        // Janela dentro do mesmo dia (ex.: 06:00–19:00)
        return nowMin >= iniMin && nowMin <= fimMin;
      }
      // Janela cruza meia-noite (ex.: 19:00–05:59)
      return nowMin >= iniMin || nowMin <= fimMin;
    } catch {
      return false;
    }
  })();

  // Violação de PA na Saída — o cadastro é PA (passagem_autorizada=true) e
  // o tempo_limite (prazo do monitoramento/PA) já expirou. Quando isso acontece,
  // a Observação do Cadastro passa a ser obrigatória para justificar a violação.
  // O backend gravará violacao_pa_saida=true no doc do movimento SAIDA.
  const obsPaViolacaoObrigatoria = (() => {
    if (!visitante?.passagem_autorizada) return false;
    if (!visitante?.tempo_limite) return false;
    const tl = new Date(visitante.tempo_limite);
    if (isNaN(tl.getTime())) return false;
    return new Date() > tl;
  })();

  const obsObrigatoria = obsCadastroObrigatoria || obsPrestadorObrigatoria || obsPaViolacaoObrigatoria;
  const motivoObrigatoriedade = obsCadastroObrigatoria
    ? "* obrigatória — saída fora da validade"
    : obsPaViolacaoObrigatoria
      ? "* obrigatória — saída fora do prazo PA (violação)"
      : "* obrigatória quando Prestador/Entregador";
  const placeholderObrigatoria = obsCadastroObrigatoria
    ? "Justifique o motivo da saída ter sido registrada fora da validade do cadastro..."
    : obsPaViolacaoObrigatoria
      ? "Justifique o motivo da saída ter sido registrada após o prazo do PA (Passagem Autorizada)..."
      : "Justifique por que este Prestador/Entregador está acessando uma portaria que não aceita esse tipo de visitante...";

  // Carrega observações ao abrir
  useEffect(() => {
    if (!open || !visitante) return;
    let canceled = false;
    setLoading(true);
    setObsCpf("");
    setObsCpfOriginal("");
    setObsCadastro("");
    setObsCadastroOriginal("");
    setHistorico([]);
    setHistoricoOpen(false);
    setObsHistorico([]);
    setObsHistoricoOpen(false);
    setObsCpfOpen(false);
    dirtyRef.current = { cpf: false, registro: false };

    const load = async () => {
      const tasks = [];

      // Obs do CPF
      if (visitante.cpf) {
        tasks.push(
          clienteApi
            .get(`/cliente/obs-cpf/${visitante.cpf}`)
            .then((r) => {
              const v = r.data?.mensagem || "";
              if (!canceled) {
                setObsCpf(v);
                setObsCpfOriginal(v);
                // Se já existe obs permanente registrada, abre o collapsible automaticamente
                if (v && v.trim()) setObsCpfOpen(true);
              }
            })
            .catch(() => {})
        );

        // Histórico das últimas 5 visitas do CPF
        tasks.push(
          clienteApi
            .get(`/cliente/visitantes/historico-cpf/${visitante.cpf}?limit=5`)
            .then((r) => {
              if (!canceled) setHistorico(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {})
        );

        // Todas as observações de registro desse CPF (todos os cadastros históricos)
        tasks.push(
          clienteApi
            .get(`/cliente/visitantes/obs-cpf/${visitante.cpf}`)
            .then((r) => {
              if (!canceled) setObsHistorico(Array.isArray(r.data) ? r.data : []);
            })
            .catch(() => {})
        );
      }

      // Obs do Registro
      tasks.push(
        clienteApi
          .get(`/cliente/visitantes/${visitante.id}/observacao`)
          .then((r) => {
            const v = r.data?.observacao || "";
            if (!canceled) {
              setObsCadastro(v);
              setObsCadastroOriginal(v);
            }
          })
          .catch(() => {})
      );

      try {
        await Promise.all(tasks);
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, [open, visitante]);

  // ESC fecha o modal
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  const persistObservacoes = async () => {
    if (!visitante) return false;
    const cpfDirty = obsCpf !== obsCpfOriginal;
    const cadDirty = obsCadastro !== obsCadastroOriginal;

    try {
      if (cadDirty) {
        await clienteApi.post(`/cliente/visitantes/${visitante.id}/observacao`, {
          cpf: visitante.cpf,
          visitante_id: visitante.id,
          observacao: obsCadastro,
        });
      }
      if (cpfDirty && visitante.cpf) {
        // POST grava se não existir; se existir, atualiza no mesmo endpoint
        await clienteApi.post(`/cliente/obs-cpf`, {
          doc_visitante: visitante.cpf,
          mensagem: obsCpf,
        });
      }
      return true;
    } catch (e) {
      console.error("Erro ao salvar observações:", e);
      toast.error("Falha ao salvar observações");
      return false;
    }
  };

  const handleSalvar = async () => {
    setSaving(true);
    const ok = await persistObservacoes();
    setSaving(false);
    if (ok) {
      toast.success("Observações salvas");
      onUpdated && onUpdated({ tipo: "observacoes" });
      handleClose();
    }
  };

  const handleRegistrarEntrada = async () => {
    // Bloqueio: Prestador/Entregador em portaria que não aceita → Observação do Cadastro obrigatória.
    if (obsPrestadorObrigatoria && !(obsCadastro || "").trim()) {
      toast.error("Observação do Cadastro obrigatória — a portaria atual não aceita Prestadores/Entregadores. Justifique o acesso.");
      return;
    }
    setSavingEntrada(true);
    try {
      const ok = await persistObservacoes();
      if (!ok) {
        setSavingEntrada(false);
        return;
      }
      // Usa novo endpoint de movimento (suporta multi-ciclo / hóspedes).
      // Para o primeiro ciclo, comporta-se igual ao endpoint legado.
      const r = await clienteApi.post(`/cliente/visitantes/${visitante.id}/movimento`, {
        tipo: "ENTRADA",
        origem: "MANUAL",
      });
      const _placa = (r.data?.visitante?.placa || visitante.placa || "").trim();
      toast.success(`Entrada registrada com sucesso${_placa ? ` — Placa ${_placa.toUpperCase()}` : ""}`);
      onUpdated && onUpdated({ tipo: "entrada", visitante: r.data?.visitante || r.data });
      handleClose();
    } catch (e) {
      console.error("Erro ao registrar entrada:", e);
      toast.error(e.response?.data?.detail || "Falha ao registrar entrada");
    } finally {
      setSavingEntrada(false);
    }
  };

  const handleRegistrarSaida = async () => {
    // Bloqueio 1: quando a saída acontece em dia diferente da validade final
    // (cadastro vencido + virou o dia), a Observação do Cadastro é obrigatória.
    if (obsCadastroObrigatoria && !(obsCadastro || "").trim()) {
      toast.error("Observação do Cadastro obrigatória — justifique a saída fora da validade.");
      return;
    }
    // Bloqueio 2: Prestador/Entregador em portaria que não aceita → obs obrigatória também na Saída.
    if (obsPrestadorObrigatoria && !(obsCadastro || "").trim()) {
      toast.error("Observação do Cadastro obrigatória — a portaria atual não aceita Prestadores/Entregadores. Justifique o acesso.");
      return;
    }
    // Bloqueio 3: Violação de PA — cadastro é PA e tempo_limite já expirou.
    if (obsPaViolacaoObrigatoria && !(obsCadastro || "").trim()) {
      toast.error("Observação do Cadastro obrigatória — saída fora do prazo PA (será marcada como violação).");
      return;
    }
    setSavingSaida(true);
    try {
      const ok = await persistObservacoes();
      if (!ok) {
        setSavingSaida(false);
        return;
      }
      // Novo endpoint multi-ciclo (registra movimento SAIDA e atualiza status_atual=FORA).
      const r = await clienteApi.post(`/cliente/visitantes/${visitante.id}/movimento`, {
        tipo: "SAIDA",
        origem: "MANUAL",
      });
      const _placa = (r.data?.visitante?.placa || visitante.placa || "").trim();
      toast.success(`Saída registrada com sucesso${_placa ? ` — Placa ${_placa.toUpperCase()}` : ""}`);
      onUpdated && onUpdated({ tipo: "saida", visitante: r.data?.visitante || r.data });
      handleClose();
    } catch (e) {
      console.error("Erro ao registrar saída:", e);
      toast.error(e.response?.data?.detail || "Falha ao registrar saída");
    } finally {
      setSavingSaida(false);
    }
  };

  const formatDT = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const formatDuracao = (entrada, saida) => {
    if (!entrada || !saida) return "-";
    try {
      const e = new Date(entrada).getTime();
      const s = new Date(saida).getTime();
      if (Number.isNaN(e) || Number.isNaN(s) || s < e) return "-";
      const min = Math.round((s - e) / 60000);
      const h = Math.floor(min / 60);
      const m = min % 60;
      if (h <= 0) return `${m}m`;
      return `${h}h${String(m).padStart(2, "0")}`;
    } catch {
      return "-";
    }
  };

  const tempoMedioVisitas = (() => {
    const completas = (historico || []).filter((h) => h.data_hora_entrada && h.data_hora_saida);
    if (completas.length === 0) return null;
    let totalMin = 0;
    for (const h of completas) {
      const e = new Date(h.data_hora_entrada).getTime();
      const s = new Date(h.data_hora_saida).getTime();
      if (!Number.isNaN(e) && !Number.isNaN(s) && s > e) {
        totalMin += (s - e) / 60000;
      }
    }
    if (totalMin <= 0) return null;
    const media = Math.round(totalMin / completas.length);
    const h = Math.floor(media / 60);
    const m = media % 60;
    return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}m`;
  })();

  if (!open || !visitante) return null;

  const veiculoLinha = [visitante.modelo_veiculo, visitante.cor_veiculo]
    .filter(Boolean)
    .join(" · ");

  // Formatadores compactos para a validade dentro do card de Veículo:
  // — exibição: inicial "dd/MM HH:mm" (sem ano), final "dd/MM/yy HH:mm" (com ano abreviado)
  // — tooltip:  período completo "dd/MM/yyyy HH:mm:ss → dd/MM/yyyy HH:mm:ss"
  const _pad = (n) => String(n).padStart(2, "0");
  const _parseDT = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const fmtCompact = (d, includeYear = false) => {
    if (!d) return "";
    const dia = _pad(d.getDate());
    const mes = _pad(d.getMonth() + 1);
    const hh = _pad(d.getHours());
    const mm = _pad(d.getMinutes());
    if (includeYear) {
      const yy = String(d.getFullYear()).slice(-2);
      return `${dia}/${mes}/${yy} ${hh}:${mm}`;
    }
    return `${dia}/${mes} ${hh}:${mm}`;
  };
  const fmtFull = (d) => {
    if (!d) return "—";
    const dia = _pad(d.getDate());
    const mes = _pad(d.getMonth() + 1);
    const ano = d.getFullYear();
    const hh = _pad(d.getHours());
    const mm = _pad(d.getMinutes());
    const ss = _pad(d.getSeconds());
    return `${dia}/${mes}/${ano} ${hh}:${mm}:${ss}`;
  };
  const piDate = _parseDT(visitante.permanencia_inicial);
  const pfDate = _parseDT(visitante.permanencia_final);
  const cicloFinalizado = !!visitante.ciclo_finalizado;
  // Estado da validade: "finalizado" tem prioridade — explica ao operador
  // porque a entrada/saída está bloqueada.
  // Demais: "vigente" | "vencida" | "futura"
  let validadeEstado = null;
  if (cicloFinalizado) {
    validadeEstado = "finalizado";
  } else if (piDate || pfDate) {
    const agora = new Date();
    if (pfDate && agora > pfDate) validadeEstado = "vencida";
    else if (piDate && agora < piDate) validadeEstado = "futura";
    else validadeEstado = "vigente";
  }
  const fmtCicloFinalizadoEm = visitante.ciclo_finalizado_em
    ? fmtFull(_parseDT(visitante.ciclo_finalizado_em))
    : "";
  const validadeTooltip = cicloFinalizado
    ? `Ciclo encerrado${fmtCicloFinalizadoEm ? ` em ${fmtCicloFinalizadoEm}` : ""}${
        visitante.ciclo_finalizado_por_nome ? ` por ${visitante.ciclo_finalizado_por_nome}` : ""
      } — registre um novo cadastro para nova entrada.`
    : piDate || pfDate
      ? `Validade: ${fmtFull(piDate)} → ${fmtFull(pfDate)}${
          validadeEstado === "vencida"
            ? " — VENCIDA"
            : validadeEstado === "futura"
              ? " — Ainda não iniciada"
              : " — Vigente"
        }`
      : "";
  const validadeClasses =
    validadeEstado === "finalizado"
      ? "bg-red-500/15 border border-red-400/40 text-red-300"
      : validadeEstado === "vencida"
        ? "bg-red-500/15 border border-red-400/40 text-red-300"
        : validadeEstado === "futura"
          ? "bg-amber-500/15 border border-amber-400/40 text-amber-200"
          : "bg-emerald-500/15 border border-emerald-400/40 text-emerald-200";
  const validadeLabelColor =
    validadeEstado === "finalizado" || validadeEstado === "vencida"
      ? "text-red-300/80"
      : validadeEstado === "futura"
        ? "text-amber-300/80"
        : "text-emerald-300/80";
  const validadeArrowColor =
    validadeEstado === "finalizado" || validadeEstado === "vencida"
      ? "text-red-300/60"
      : validadeEstado === "futura"
        ? "text-amber-300/60"
        : "text-emerald-300/60";

  const inputCls =
    "w-full rounded-md bg-white/10 border border-brand-ice/20 text-brand-ice placeholder:text-brand-ice/40 focus:outline-none focus:ring-2 focus:ring-brand-light/40 focus:border-brand-light p-2.5 text-sm";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        // clique fora não fecha (mais seguro pra evitar perda do digitado)
        e.stopPropagation();
      }}
      data-testid="visitante-obs-saida-modal"
    >
      <div className="bg-[#0f1a30] border border-brand-ice/20 rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-brand-ice/10">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-brand-medium/30 text-brand-light">
              <FileEdit className="w-5 h-5" />
            </div>
            <div>
              <h3
                className="font-heading font-bold text-brand-ice text-lg leading-tight"
                data-testid="modal-visitante-nome"
              >
                {visitante.nome || "Visitante"}
              </h3>
              {visitante.cpf && (
                <p className="text-xs text-brand-ice/60 mt-0.5">CPF {visitante.cpf}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {visitante.unidade && (
              <span
                data-testid="modal-visitante-eql"
                title="EQL / Unidade"
                className="font-heading font-extrabold uppercase tracking-wider text-brand-light text-xl leading-none"
              >
                {String(visitante.unidade).toUpperCase()}
              </span>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="p-1.5 rounded-lg text-brand-ice/60 hover:text-brand-ice hover:bg-white/10 transition-colors"
              title="Fechar (Esc)"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Veículo */}
          {(visitante.placa || piDate || pfDate || cicloFinalizado) && (
            <div className="bg-white/5 rounded-lg p-3 border border-brand-ice/10" data-testid="modal-veiculo">
              <div className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-heading font-bold mb-1">
                Veículo
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                {visitante.placa && (
                  <span className="font-mono text-base font-bold text-brand-ice">
                    {visitante.placa}
                  </span>
                )}
                {veiculoLinha && (
                  <span className="text-sm text-brand-ice/70">{veiculoLinha}</span>
                )}
                {visitante.vaga && (
                  <span className="text-xs text-brand-ice/60">
                    Vaga: <span className="font-mono text-brand-ice">{visitante.vaga}</span>
                  </span>
                )}
                {(piDate || pfDate || cicloFinalizado) && (
                  <span
                    data-testid="modal-veiculo-validade"
                    data-validade-estado={validadeEstado}
                    title={validadeTooltip}
                    className={`ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-mono ${validadeClasses}`}
                  >
                    <span className={`uppercase tracking-wider text-[9px] font-heading font-bold pr-1 ${validadeLabelColor}`}>
                      Validade
                    </span>
                    {piDate ? fmtCompact(piDate, false) : "—"}
                    <span className={validadeArrowColor}>→</span>
                    {pfDate ? fmtCompact(pfDate, true) : "—"}
                    {cicloFinalizado && (
                      <span className="ml-1.5 pl-1.5 border-l border-red-400/30 font-heading font-bold tracking-wider uppercase text-[9px]">
                        Finalizado
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Datas */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white/5 rounded-lg p-3 border border-brand-ice/10">
              <div className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-heading font-bold mb-1">
                Cadastro
              </div>
              <div className="text-sm text-brand-ice flex items-center gap-2 flex-wrap" data-testid="modal-data-cadastro">
                <span>{formatDT(visitante.data_cadastro)}</span>
                {visitante.passagem_autorizada === true && (
                  <span
                    data-testid="modal-obs-pa-badge"
                    className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30 font-heading font-bold tracking-wider"
                    title="Passagem Autorizada"
                  >
                    PA
                  </span>
                )}
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-brand-ice/10">
              <div className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-heading font-bold mb-1">
                Entrada
              </div>
              <div className="text-sm text-brand-ice" data-testid="modal-data-entrada">
                {visitante.data_hora_entrada ? formatDT(visitante.data_hora_entrada) : (
                  <span className="text-brand-ice/50">Não registrada</span>
                )}
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-brand-ice/10">
              <div className="text-[10px] uppercase tracking-wider text-brand-ice/60 font-heading font-bold mb-1">
                Saída
              </div>
              <div className="text-sm text-brand-ice" data-testid="modal-data-saida">
                {visitante.data_hora_saida ? formatDT(visitante.data_hora_saida) : (
                  <span className="text-brand-ice/50">Não registrada</span>
                )}
              </div>
            </div>
          </div>

          {/* Observação do Cadastro (primeiro — caso mais comum) */}
          <div>
            <label className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70 mb-1.5 block">
              Observação do Cadastro (apenas este registro)
              {obsObrigatoria && (
                <span data-testid="modal-obs-cadastro-obrigatoria" className="ml-2 text-red-400 normal-case tracking-normal">
                  {motivoObrigatoriedade}
                </span>
              )}
            </label>
            <textarea
              value={obsCadastro}
              onChange={(e) => setObsCadastro(e.target.value)}
              placeholder={loading
                ? "Carregando..."
                : (obsObrigatoria
                  ? placeholderObrigatoria
                  : "Observação visível somente neste cadastro de visita")}
              rows={3}
              className={`w-full rounded-md bg-white/25 border text-brand-ice placeholder:text-brand-ice/50 focus:outline-none focus:ring-2 p-2.5 text-sm font-medium ${
                obsObrigatoria
                  ? "border-red-400/60 focus:ring-red-400/40 focus:border-red-400"
                  : "border-brand-ice/30 focus:ring-brand-light/40 focus:border-brand-light"
              }`}
              disabled={loading}
              data-testid="modal-obs-cadastro"
            />
            <div className="flex justify-end mt-1">
              <button
                type="button"
                onClick={() => setMsgObsManagerOpen(true)}
                title="Mensagens pré-cadastradas"
                data-testid="modal-obs-cadastro-msg-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-brand-ice/60 hover:text-brand-light hover:bg-white/10 transition-colors"
              >
                <MessageSquareText className="w-3.5 h-3.5" />
                <span>Mensagens</span>
                <span className="font-bold text-sm leading-none" aria-hidden="true">+</span>
              </button>
            </div>
          </div>

          {/* Observação Permanente (collapsible — fechado por padrão) */}
          <div data-testid="modal-obs-cpf-container">
            <button
              type="button"
              onClick={() => setObsCpfOpen((v) => !v)}
              data-testid="modal-obs-cpf-toggle"
              aria-expanded={obsCpfOpen}
              className="w-full flex items-center justify-between gap-2 py-2 px-1 rounded hover:bg-white/5 transition-colors group"
            >
              <span className="flex items-center gap-2">
                {obsCpfOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />
                  : <ChevronRight className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />}
                <span className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70 group-hover:text-brand-ice">
                  Observação Permanente
                </span>
                {(obsCpf || "").trim() && (
                  <span
                    title="Existe observação permanente registrada"
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-heading font-extrabold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/30"
                  >
                    preenchida
                  </span>
                )}
              </span>
            </button>
            {obsCpfOpen && (
              <textarea
                value={obsCpf}
                onChange={(e) => setObsCpf(e.target.value)}
                placeholder={loading ? "Carregando..." : "Observação atrelada ao CPF — aparece em todos os cadastros desse visitante"}
                rows={3}
                className={`${inputCls} mt-1.5`}
                disabled={loading || !visitante.cpf}
                data-testid="modal-obs-cpf"
              />
            )}
          </div>

          {/* Histórico de OBSERVAÇÕES deste CPF (collapsible — fechado por padrão) */}
          {visitante.cpf && (
            <div data-testid="modal-obs-historico">
              <button
                type="button"
                onClick={() => setObsHistoricoOpen((v) => !v)}
                data-testid="modal-obs-historico-toggle"
                aria-expanded={obsHistoricoOpen}
                className="w-full flex items-center justify-between gap-2 py-2 px-1 rounded hover:bg-white/5 transition-colors group"
              >
                <span className="flex items-center gap-2">
                  {obsHistoricoOpen
                    ? <ChevronDown className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />
                    : <ChevronRight className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />}
                  <span className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70 group-hover:text-brand-ice">
                    Todas observações deste Visitante
                  </span>
                  <span className="text-[10px] text-brand-ice/50">({obsHistorico.length})</span>
                </span>
              </button>
              {obsHistoricoOpen && (
                obsHistorico.length === 0 ? (
                  <div className="text-xs text-brand-ice/50 bg-white/5 rounded-lg p-3 border border-brand-ice/10 mt-1.5">
                    {loading ? "Carregando observações..." : "Nenhuma observação registrada para este visitante."}
                  </div>
                ) : (
                  <div className="bg-white/5 rounded-lg border border-brand-ice/10 overflow-hidden mt-1.5 max-h-56 overflow-y-auto">
                    <ul className="divide-y divide-brand-ice/10">
                      {obsHistorico.map((o) => (
                        <li key={o.visitante_id} data-testid={`obs-row-${o.visitante_id}`} className="p-2.5 hover:bg-white/5">
                          <div className="text-[10px] font-mono text-brand-ice/60 mb-1">
                            {formatDT(o.data_alteracao)}
                          </div>
                          <div className="text-xs text-brand-ice whitespace-pre-wrap break-words">
                            {o.observacao}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>
          )}

          {/* Histórico das últimas 5 visitas (collapsible — fechado por padrão) */}
          {visitante.cpf && (
            <div data-testid="modal-historico">
              <button
                type="button"
                onClick={() => setHistoricoOpen((v) => !v)}
                data-testid="modal-historico-toggle"
                aria-expanded={historicoOpen}
                className="w-full flex items-center justify-between gap-2 py-2 px-1 rounded hover:bg-white/5 transition-colors group"
              >
                <span className="flex items-center gap-2">
                  {historicoOpen
                    ? <ChevronDown className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />
                    : <ChevronRight className="w-3.5 h-3.5 text-brand-ice/60 group-hover:text-brand-ice" />}
                  <span className="text-xs uppercase tracking-wider font-heading font-bold text-brand-ice/70 group-hover:text-brand-ice">
                    Últimas visitas deste CPF
                  </span>
                  <span className="text-[10px] text-brand-ice/50">({historico.length})</span>
                </span>
                {tempoMedioVisitas && (
                  <span className="text-[10px] text-brand-ice/60">
                    Tempo médio: <span className="text-brand-light font-bold">{tempoMedioVisitas}</span>
                  </span>
                )}
              </button>
              {historicoOpen && (
                historico.length === 0 ? (
                  <div className="text-xs text-brand-ice/50 bg-white/5 rounded-lg p-3 border border-brand-ice/10 mt-1.5">
                    {loading ? "Carregando histórico..." : "Sem visitas anteriores registradas."}
                  </div>
                ) : (
                  <div className="bg-white/5 rounded-lg border border-brand-ice/10 overflow-hidden mt-1.5">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5">
                        <tr className="text-brand-ice/60 text-[10px] uppercase tracking-wider">
                          <th className="text-left py-2 px-2 font-heading font-bold">Cadastro</th>
                          <th className="text-left py-2 px-2 font-heading font-bold">Entrada</th>
                          <th className="text-left py-2 px-2 font-heading font-bold">Saída</th>
                          <th className="text-left py-2 px-2 font-heading font-bold">EQL</th>
                          <th className="text-right py-2 px-2 font-heading font-bold">Duração</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historico.map((h) => {
                          const isCurrent = h.id === visitante.id;
                          return (
                            <tr
                              key={h.id}
                              data-testid={`hist-row-${h.id}`}
                              className={`border-t border-brand-ice/10 ${isCurrent ? "bg-brand-light/10" : ""}`}
                            >
                              <td className="py-1.5 px-2 text-brand-ice/80 whitespace-nowrap">{formatDT(h.data_cadastro)}</td>
                              <td className="py-1.5 px-2 text-brand-ice/80 whitespace-nowrap">{h.data_hora_entrada ? formatDT(h.data_hora_entrada) : <span className="text-brand-ice/40">-</span>}</td>
                              <td className="py-1.5 px-2 text-brand-ice/80 whitespace-nowrap">{h.data_hora_saida ? formatDT(h.data_hora_saida) : <span className="text-brand-ice/40">-</span>}</td>
                              <td className="py-1.5 px-2 text-brand-ice font-mono whitespace-nowrap" title={h.unidade || ""}>{h.unidade || <span className="text-brand-ice/40">-</span>}</td>
                              <td className="py-1.5 px-2 text-right font-mono text-brand-ice whitespace-nowrap">
                                {formatDuracao(h.data_hora_entrada, h.data_hora_saida)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-end gap-2 p-4 border-t border-brand-ice/10 bg-white/5 rounded-b-2xl">
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-brand-ice/80 hover:text-brand-ice hover:bg-white/10 text-sm font-heading font-bold transition-colors"
            data-testid="modal-cancelar-btn"
          >
            <X className="w-4 h-4" /> Cancelar
          </button>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={saving || savingSaida || savingEntrada || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-medium hover:bg-brand-dark text-white text-sm font-heading font-bold transition-colors disabled:opacity-60"
            data-testid="modal-salvar-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
          {/* Decisão dos botões: prioriza status_atual (multi-ciclo) sobre os caches data_hora_entrada/saida (1-ciclo legado) */}
          {(() => {
            const statusUpper = (visitante.status_atual || "").toUpperCase();
            const encerrado = statusUpper === "ENCERRADO" || !!visitante.ciclo_finalizado;
            // Validade expirou? (datetime completo, respeita mesmo dia + hora ultrapassada)
            const expirou = (() => {
              if (!visitante?.permanencia_final) return false;
              const pf = new Date(visitante.permanencia_final);
              if (isNaN(pf.getTime())) return false;
              return new Date() > pf;
            })();
            // DENTRO → só Saída. Qualquer outro status ativo (FORA, AGENDADO — visitante
            // recém-confirmado de agenda, "" ou legado sem status) → decide pelos caches
            // data_hora_entrada/saida, igual ao card. ENTRADA é bloqueada se a validade expirou
            // ou o ciclo já foi encerrado.
            const mostrarEntrada = !encerrado && !expirou
              && (statusUpper === "FORA" || (statusUpper !== "DENTRO" && !visitante.data_hora_entrada));
            const mostrarSaida = !encerrado
              && (statusUpper === "DENTRO"
                || (statusUpper !== "FORA" && !!visitante.data_hora_entrada && !visitante.data_hora_saida));
            return (
              <>
                {mostrarEntrada && (
                  <button
                    type="button"
                    onClick={handleRegistrarEntrada}
                    disabled={savingEntrada || saving || savingSaida || loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading font-bold transition-colors disabled:opacity-60"
                    title="Salvar e registrar entrada"
                    data-testid="modal-registrar-entrada-btn"
                  >
                    <LogIn className="w-4 h-4" /> {savingEntrada ? "Registrando..." : "Registrar Entrada"}
                  </button>
                )}
                {mostrarSaida && (
                  <button
                    type="button"
                    onClick={handleRegistrarSaida}
                    disabled={savingSaida || saving || savingEntrada || loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading font-bold transition-colors disabled:opacity-60"
                    title="Salvar e registrar saída"
                    data-testid="modal-registrar-saida-btn"
                  >
                    <LogOut className="w-4 h-4" /> {savingSaida ? "Registrando..." : "Registrar Saída"}
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
      <MsgObsAcessoManagerModal
        open={msgObsManagerOpen}
        onOpenChange={setMsgObsManagerOpen}
        onPick={(texto) => {
          if (!texto) return;
          setObsCadastro((prev) => {
            const atual = (prev || "").trim();
            return atual ? `${atual}\n${texto}` : texto;
          });
        }}
      />
    </div>
  );
}
